'use client';

import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils/cn';

// Indicateur visuel de force du mot de passe — 4 pips + label.
//
// Critères (chaque vrai ajoute +1 au score, max 4) :
//   • longueur ≥ 8
//   • au moins une majuscule
//   • au moins un chiffre
//   • au moins un caractère spécial
//
// Réutilisé par :
//   • le sign-up (`auth-signup-feature.tsx`)
//   • le set/change password dans Settings ▸ Sécurité (`account-password-card.tsx`)
//
// Les labels vivent dans `auth.password.strength` — namespace gardé tel quel
// pour éviter de toucher aux clés i18n existantes.

type Props = {
  value: string;
};

function score(pwd: string): number {
  let s = 0;
  if (pwd.length >= 8) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return s;
}

const LABEL_KEYS = ['veryWeak', 'weak', 'ok', 'strong', 'excellent'] as const;

const PIP_CLASS = [
  'bg-neutral-200',
  'bg-danger-500',
  'bg-warning-500',
  'bg-purple-400',
  'bg-success-500',
] as const;

const TEXT_CLASS = [
  'text-fg-4',
  'text-danger-700',
  'text-warning-700',
  'text-purple-700',
  'text-success-700',
] as const;

export function PasswordStrength({ value }: Props) {
  const t = useTranslations('auth.password.strength');
  const s = score(value);
  const label = t(LABEL_KEYS[s]);

  return (
    <div className="mt-2 flex items-center gap-2.5 px-1">
      <div className="flex flex-1 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors duration-200 ease-out',
              i < s ? PIP_CLASS[s] : 'bg-neutral-200',
            )}
          />
        ))}
      </div>
      <span className={cn('min-w-[60px] text-right text-[11px] font-medium', TEXT_CLASS[s])}>
        {label}
      </span>
    </div>
  );
}
